import { type ClassValue, clsx } from 'clsx'
import { format, parse } from 'date-fns'
import { twMerge } from 'tailwind-merge'

type StopTypes = number | string | boolean | symbol | bigint | Date

type ExcludedTypes = (...args: unknown[]) => unknown
type Dot<T extends string, U extends string> = '' extends U ? T : `${T}.${U}`

export type GetKeys<T> = T extends StopTypes
  ? ''
  : T extends readonly unknown[]
    ? GetKeys<T[number]>
    : {
        [K in keyof T & string]: T[K] extends StopTypes
          ? K
          : T[K] extends ExcludedTypes
            ? never
            : K | Dot<K, GetKeys<T[K]>>
      }[keyof T & string]

export const getDescendantProp = <T, R>(obj: T, path: GetKeys<T>): R | undefined => {
  const parts = (path as string).split('.')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return parts.reduce((acc, part) => acc && (acc as any)[part], obj) as R | undefined
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type ValueOf<T> = T[keyof T]

export function getKeyByValue<T extends Record<string, unknown>>(object: T, value: ValueOf<T>): keyof T | undefined {
  return Object.keys(object).find((key) => object[key] === value)
}

export function truncate(text: string, max?: number) {
  max = max || 10
  return text.substring(0, max - 1) + (text.length > max ? '...' : '')
}

export function convertTimeTo24(time: string) {
  const parsedTime = parse(time, 'hh:mm aa', new Date())
  return format(parsedTime, 'HH:mm')
}
